import React from 'react';
import {
  Lang,
  DEFAULT_CONTENT_TAGS_OPTIONS,
  TAGS_LAB_CONTENT_TYPE,
  EVENTS,
  ENTITY_TYPES,
} from 'shared/constants';
import { UploadDocument } from 'src/components/common';
import styles from './lab-value-editor.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MrkdwnEditor } from 'src/components/core';
import { TextInput, SelectBox } from 'src/components/design-system';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { TagsSelector, TAG_SELECTOR_TYPE } from 'src/components/tags/tagSelector';
import { DrugClassSelector } from 'src/components/drugClass/drugClassSelector';
import { Modal } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';

const LAB_VALUE_SCHEMA = Yup.object().shape({
  title: Yup.string().min(2, 'Too Short!').max(200, 'Too Long!').required('Required!'),
  value: Yup.string().min(2, 'Too Short!').required('Required!'),
  keywords: Yup.array().of(Yup.string()),
  info: Yup.string(),
  reference: Yup.string().url(),
  url: Yup.string().url(),
  ownerIds: Yup.array()
    .of(Yup.string())
    .min(1, 'Please assign at least one owner')
    .required('Required!'),
  specialty: Yup.string().nullable(),
  subspecialty: Yup.string().nullable(),
  contentType: Yup.string().nullable(),
  targetAudience: Yup.array().of(Yup.string()),
  drugClass: Yup.string().nullable(),
});

const LabValueEditorComponent = React.memo(function LabValueEditor({
  open = false,
  lang = Lang.LAB_VALUE_EDITOR,
  value,
  categoryId = null,
  createLabItem = () => {},
  updateLabItem = () => {},
  onFinish = () => {},
}) {
  value = value ?? {};
  const isNew = !value.id;
  const [isLoading, setLoadingState] = React.useState(false);

  const updateOrGenerateValue = React.useCallback(
    async item => {
      setLoadingState(true);

      const { specialty, subspecialty, contentType, targetAudience, ...restData } = item;

      const itemData = {
        item: {
          ...restData,
          tags: { specialty, subspecialty, contentType, targetAudience },
          drugClass: contentType === TAGS_LAB_CONTENT_TYPE.DRUG_DOSAGE ? item.drugClass : null,
        },
      };

      if (categoryId) {
        itemData.categoryId = categoryId;
      }
      if (isNew) {
        await createLabItem(itemData);
        analyticsService.track(EVENTS.CONTENT.UPLOAD, {
          entityType: ENTITY_TYPES.compendiumItem,
        });
      } else {
        itemData.id = value.id;
        analyticsService.track(EVENTS.CONTENT.UPDATE, {
          entityType: ENTITY_TYPES.compendiumItem,
        });

        await updateLabItem(itemData);
      }

      onFinish(true);
      setLoadingState(false);
    },
    [categoryId, createLabItem, isNew, onFinish, updateLabItem, value.id],
  );

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    values,
    errors,
    touched,
    dirty,
    isValid,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: LAB_VALUE_SCHEMA,
    initialValues: {
      ...value,
      ...value.tags,
      drugClass:
        value?.tags?.contentType === TAGS_LAB_CONTENT_TYPE.DRUG_DOSAGE ? value.drugClass : null,
      ownerIds: isNew ? [] : value.ownerIds || [],
    },
    onSubmit: updateOrGenerateValue,
  });

  const dismiss = React.useCallback(() => {
    // TODO: analytics

    onFinish();
  }, [onFinish]);

  const resetField = React.useCallback(
    field => {
      // Clear the dependent field through Formik. resetForm() repointed initialValues at the
      // (mutated) current values, which dropped `dirty` and — with enableReinitialize — made the
      // next effect snap the whole form back to the entity, reverting the value the user had just
      // cleared. That made the specialty clear-X a no-op. See BB-17.
      setFieldValue(field, null);
    },
    [setFieldValue],
  );

  return (
    <Modal
      title={isNew ? lang.CREATE_NEW_TITLE : lang.UPDATE_TITLE}
      open={open}
      onCancel={dismiss}
      onSubmit={handleSubmit}
      cancelText={lang.DISMISS}
      submitText={isNew ? lang.CREATE : lang.UPDATE}
      loading={isLoading}
      submitDisabled={!dirty || !isValid || isLoading}
      width={600}
      styles={{
        body: {
          maxHeight: '75vh',
          overflowY: 'auto',
        },
      }}>
      <form onSubmit={handleSubmit} data-testid="compendium-item-form">
        <TextInput
          name="title"
          label={lang.ITEM_TITLE}
          value={values.title}
          error={errors.title}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={lang.ITEM_TITLE_PLACEHOLDER}
          required
        />

        <label>{lang.ITEM_VALUES}</label>
        <MrkdwnEditor
          name="value"
          value={values.value}
          error={errors.value}
          touched={touched.value}
          onChange={setFieldValue}
          placeholder={lang.ITEM_VALUES_PLACEHOLDER}
        />

        <MultipleOwnersSelector
          label={lang.OWNER}
          size="large"
          value={values.ownerIds}
          error={errors.ownerIds}
          placeholder={lang.OWNER_PLACEHOLDER}
          name="ownerIds"
          onChange={setFieldValue}
          borderRadius={4}
          parentOwnerIds={value?.ownerIds}
        />

        <SelectBox
          label={lang.ITEM_KEYWORDS}
          value={values.keywords}
          options={DEFAULT_CONTENT_TAGS_OPTIONS}
          name="keywords"
          placeholder={lang.ITEM_KEYWORDS_PLACEHOLDER}
          onChange={setFieldValue}
          mode="multiple"
        />

        <TagsSelector
          selectedValues={values}
          errors={errors}
          onChange={setFieldValue}
          resetField={resetField}
          type={TAG_SELECTOR_TYPE.LAB}
        />

        {values.contentType === TAGS_LAB_CONTENT_TYPE.DRUG_DOSAGE && (
          <>
            <DrugClassSelector
              label={lang.DRUG_CLASS}
              value={values.drugClass}
              error={errors.drugClass}
              placeholder={lang.DRUG_CLASS_PLACEHOLDER}
              name="drugClass"
              onChange={setFieldValue}
              resetField={resetField}
            />
          </>
        )}

        <label>{lang.ITEM_INFO}</label>
        <MrkdwnEditor
          name="info"
          value={values.info}
          error={errors.info}
          touched={touched.info}
          onChange={setFieldValue}
          placeholder={lang.ITEM_INFO_PLACEHOLDER}
        />

        <label>{lang.ITEM_REFERENCE}</label>
        <UploadDocument
          setFieldValue={setFieldValue}
          name="reference"
          value={values.reference}
          error={errors.reference}
          touched={touched.reference}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={lang.ITEM_REFERENCE_PLACEHOLDER}
        />

        <label>{lang.ITEM_URL}</label>
        <UploadDocument
          setFieldValue={setFieldValue}
          name="url"
          value={values.url}
          error={errors.url}
          touched={touched.url}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={lang.ITEM_URL_PLACEHOLDER}
        />

        {!isNew && <span className={styles.itemId}>Id: {value?.id}</span>}
      </form>
    </Modal>
  );
});

export { LabValueEditorComponent };
